import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// import { transformFormDataToBackend } from "../../utils/transformFormData";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../utils/LoaderContext";
import { notifySuccess, notifyError } from "../../utils/ToastifyProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ORDERS_PER_PAGE = 20;

const getTokenOrThrow = () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");
  return token;
};

const cleanParams = (params) => {
  const result = {};
  Object.keys(params).forEach((key) => {
    if (
      params[key] !== "" &&
      params[key] !== undefined &&
      params[key] !== null
    ) {
      result[key] = params[key];
    }
  });
  return result;
};

// ---------------------- Get All Orders ----------------------
const fetchAllOrders = async ({
  currentPage,
  ORDERS_PER_PAGE,
  status,
  rtoStatus,
  search = "",
  startDate,
  endDate,
  downloadStatus,
  PaymentType,
  verify,
  decline,
  usersid,
}) => {
  // console.log("🚀 fetchAllOrders CALLED WITH:", {
  //   currentPage,
  //   ORDERS_PER_PAGE,
  //   status,
  //   rtoStatus,
  //   search,
  //   startDate,
  //   endDate,
  //   downloadStatus,
  //   PaymentType,
  //   verify,
  //   decline,
  //   usersid,
  // });

  const token = getTokenOrThrow();

  // Get logged-in ADMIN ID
  const adminId = localStorage.getItem("userId");
  if (!adminId) throw new Error("User ID missing");

  const url = `${BACKEND_URL}/orders/all-orders/${adminId}`;

  // Remove empty filters
  const params = cleanParams({
    page: currentPage,
    limit: ORDERS_PER_PAGE,
    tab: status,
    search,
    rtoStatus,
    startDate,
    endDate,
    downloadStatus,
    PaymentType,
    verify,
    decline,
    usersid,
  });

  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      role: localStorage.getItem("role"), // force backend to apply usersid filtering
      userid: localStorage.getItem("userId"),
    },
    params,
  });

  return data;
};

export const useAllOrders = ({
  currentPage = 1,
  ORDERS_PER_PAGE = 20,
  status = "",
  rtoStatus,
  search = "",
  startDate,
  endDate,
  downloadStatus = "",
  PaymentType = "",
  verify,
  decline,
  usersid,
}) => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const adminId = localStorage.getItem("userId");

  // 👉 Auto-get from localStorage if not passed
  const localUserId = localStorage.getItem("userId");

  return useQuery({
    queryKey: [
      "allOrders",
      currentPage,
      ORDERS_PER_PAGE,
      status,
      search,
      startDate,
      endDate,
      downloadStatus,
      rtoStatus,
      PaymentType,
      verify,
      decline,
      localUserId, // <-- use auto-userid here
    ],

    queryFn: async () => {
      setLoading(true);

      try {
        const response = await fetchAllOrders({
          currentPage,
          ORDERS_PER_PAGE,
          status,
          search,
          startDate,
          endDate,
          downloadStatus,
          rtoStatus,
          PaymentType,
          verify,
          decline,
          usersid: localUserId, // <-- Use auto-userid here
        });

        return response;
      } catch (err) {
        throw err;
      } finally {
        setLoading(false);
      }
    },

    enabled: !!adminId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,

    onError: (error) => {
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to fetch orders.");
      }
    },
  });
};

const fetchusersOrders = async ({
  currentPage,
  ORDERS_PER_PAGE,
  status,
  rtoStatus,
  search = "",
  startDate,
  endDate,
  downloadStatus,
  PaymentType,
  verify,
  decline,
  usersid,
}) => {
  // console.log("🚀 fetchAllOrders CALLED WITH:", {
  //   currentPage,
  //   ORDERS_PER_PAGE,
  //   status,
  //   rtoStatus,
  //   search,
  //   startDate,
  //   endDate,
  //   downloadStatus,
  //   PaymentType,
  //   verify,
  //   decline,
  //   usersid,
  // });

  const token = getTokenOrThrow();

  // Get logged-in ADMIN ID
  const adminId = localStorage.getItem("userId");
  if (!adminId) throw new Error("User ID missing");

  const url = `${BACKEND_URL}/orders/all-orders/${adminId}`;

  // Remove empty filters
  const params = cleanParams({
    page: currentPage,
    limit: ORDERS_PER_PAGE,
    tab: status,
    search,
    rtoStatus,
    startDate,
    endDate,
    downloadStatus,
    PaymentType,
    verify,
    decline,
    usersid,
  });

  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      role: "user", // force backend to apply usersid filtering
      userid: localStorage.getItem("userId"),
    },
    params,
  });

  return data;
};

export const useUserAllOrders = ({
  currentPage = 1,
  ORDERS_PER_PAGE = 20,
  status = "",
  rtoStatus,
  search = "",
  startDate,
  endDate,
  downloadStatus = "",
  PaymentType = "",
  verify,
  decline,
  usersid,
}) => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const adminId = localStorage.getItem("userId");
  // console.log("🔑 useAllOrders → logged-in userId:", adminId);

  return useQuery({
    queryKey: [
      "allOrders",
      currentPage,
      ORDERS_PER_PAGE,
      status,
      search,
      startDate,
      endDate,
      downloadStatus,
      rtoStatus,
      PaymentType,
      verify,
      decline,
      usersid,
    ],

    queryFn: async () => {
      setLoading(true);

      // console.log("⚡ Running queryFn with params:", {
      //   currentPage,
      //   ORDERS_PER_PAGE,
      //   status,
      //   search,
      //   startDate,
      //   endDate,
      //   downloadStatus,
      //   rtoStatus,
      //   PaymentType,
      //   verify,
      //   decline,
      //   usersid,
      // });

      try {
        const response = await fetchusersOrders({
          currentPage,
          ORDERS_PER_PAGE,
          status,
          search,
          startDate,
          endDate,
          downloadStatus,
          rtoStatus,
          PaymentType,
          verify,
          decline,
          usersid,
        });

        return response;
      } catch (err) {
        throw err;
      } finally {
        setLoading(false);
      }
    },

    enabled: !!adminId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,

    onError: (error) => {
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to fetch orders.");
      }
    },
  });
};

// ---------------------- Generate Order Number ----------------------
const fetchGeneratedOrderNumber = async () => {
  const token = getTokenOrThrow();

  // ⬇️ Fetch from localStorage
  const firstName = localStorage.getItem("firstName");
  const lastName = localStorage.getItem("lastName");

  console.log("Generating order number for:", firstName, lastName);

  const res = await axios.get(`${BACKEND_URL}/orders/generate-order-number`, {
    headers: {
      Authorization: `Bearer ${token}`,
      firstName,
      lastName,
    },
  });

  return res.data?.orderNumber;
};

export const useGenerateOrderNumber = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["generatedOrderNumber"],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchGeneratedOrderNumber();
      } finally {
        setLoading(false);
      }
    },
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      setLoading(false);
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to generate order number.");
      }
    },
  });
};

const fetchGeneratePickup = async (shipment_id) => {
  const token = getTokenOrThrow();

  const res = await axios.post(
    `${BACKEND_URL}/orders/generate-pickup`,
    { shipment_id: [shipment_id] },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.data;
};

export const useGeneratePickup = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (shipment_id) => {
      setLoading(true);
      return await fetchGeneratePickup(shipment_id);
    },

    onSuccess: (data) => {
      console.log("Pickup generated successfully:", data);
      notifySuccess("Pickup generated: " + data?.shipment_id?.[0]);
      setLoading(false);
      navigate("/orders?tab=manifest");
    },

    onError: (error) => {
      setLoading(false);

      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to generate pickup.");
      }
    },

    onSettled: () => setLoading(false),
  });
};

// ---------------------- Create Order ----------------------
const createOrder = async (submitData) => {
  const token = getTokenOrThrow();

  const response = await axios.post(
    `${BACKEND_URL}/orders/create`,
    submitData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
        userid: localStorage.getItem("userId"),
      },
    }
  );
  console.log("✅ Order created:", response.data);
  notifySuccess("Order created successfully!");
  return response.data;
};

export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (formData) => {
      setLoading(true);
      try {
        return await createOrder(formData);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["allOrders"]);
      navigate("/orders?tab=shipping");
    },
    onError: (error) => {
      setLoading(false);
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to create order.");
        console.error(
          "❌ Order creation failed:",
          error.response?.data || error
        );
      }
    },
  });
};

// ---------------------- Cancel Order ----------------------
export const cancelOrder = async (orderId) => {
  const token = getTokenOrThrow();

  console.log("Cancelling order ID:", orderId);

  const res = await axios.post(
    `${BACKEND_URL}/orders/cancel`,
    {
      order_ids: [String(orderId)],
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  notifySuccess("Order cancelled successfully!");
  return res.data;
};

export const useCancelOrder = () => {
  const queryClient = useQueryClient();
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (shiprocketOrderId) => {
      setLoading(true);
      try {
        return await cancelOrder(shiprocketOrderId);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["allOrders"]);
      navigate("/orders?tab=cancelled")
    },
    onError: (error) => {
      setLoading(false);
      console.error("❌ Cancel order failed:", error);
      notifyError(error?.response?.data?.message || "Failed to cancel order.");
    },
  });
};

// ---------------------- Fetch All Products ----------------------
const fetchProducts = async () => {
  const token = getTokenOrThrow();
  const res = await axios.get(`${BACKEND_URL}/products`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data.products;
};

export const useAllProducts = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchProducts();
      } finally {
        setLoading(false);
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    onError: (error) => {
      setLoading(false);
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to fetch products.");
      }
    },
  });
};

// ---------------------- Fetch Pincode Details ----------------------
const fetchPincodeDetails = async (pincode) => {
  const apiKey = "8e4bc4a08ac84c0a871094ddd50280a7"; // Replace with your real API key
  const url = `https://api.geoapify.com/v1/geocode/search?text=${pincode}&filter=countrycode:in&apiKey=${apiKey}`;

  const res = await axios.get(url);
  const feature = res.data.features?.[0];

  if (!feature) throw new Error("Invalid pincode or no data found");

  const { state, state_district, country } = feature.properties;

  return {
    state: state,
    city: state_district,
    country: country,
  };
};

export const usePincodeDetails = (pincode) => {
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["pincodeDetails", pincode],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchPincodeDetails(pincode);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!pincode,
    retry: 1,
    staleTime: 10 * 60 * 1000,
    onError: (error) => {
      setLoading(false);
      console.error("❌ Pincode fetch failed:", error);
      notifyError("Failed to fetch pincode details.");
    },
  });
};

const fetchPincodeServiceability = async (pincode) => {
  const res = await axios.post(`${BACKEND_URL}/orders/check-pincode`, {
    pincode,
  });
  return res.data;
};

export const useCheckPincodeServiceability = (pincode) => {
  return useQuery({
    queryKey: ["checkPincode", pincode],
    queryFn: () => fetchPincodeServiceability(pincode),
    enabled: !!pincode && pincode.length === 6, // Only run if pincode is 6 digits
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// ---------------------- Get Available Courier Partners ----------------------
export const getAvailablePartners = async (payload) => {
  try {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");

    if (!token) throw new Error("Authorization token missing");

    console.log("📦 Sending payload to backend:", payload);

    const res = await axios.post(
      `${BACKEND_URL}/orders/check-serviceability`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("🚚 Backend Response:", res.data);

    // ⭐ FIXED: Always return the list ONLY
    const courierList = res.data.data.data.available_courier_companies;

    console.log("📦 Extracted List:", courierList);

    return courierList;
  } catch (error) {
    console.error("❌ Serviceability API error:", error);
    throw error;
  }
};

// ---------------------- Get Available AWB - ASSIGN (Shipping) ----------------------
export const assignAwb = async (payload) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authorization token missing");

    console.log("🟦 Assign AWB Payload:", payload);

    const res = await axios.post(`${BACKEND_URL}/orders/assign-awb`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return res.data;
  } catch (error) {
    console.error("❌ assignAwb error:", error);
    throw error;
  }
};

export const useAssignAwb = () => {
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (payload) => {
      if (!payload || typeof payload !== "object") {
        console.warn("⏸ AWB assign skipped: invalid payload");
        return null;
      }
      setLoading(true);
      try {
        return await assignAwb(payload);
      } finally {
        setLoading(false);
      }
    },
    retry: false, // no retry
  });
};

export const assignAwbAndPickup = async (payload) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authorization token missing");

    console.log("🟦 Assign AWB & Pickup Payload:", payload);

    const res = await axios.post(`${BACKEND_URL}/orders/assign-pickup`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return res.data;
  } catch (error) {
    console.error("❌ assignAwbAndPickup error:", error);
    throw error;
  }
};

export const useAssignAwbAndPickup = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (payload) => {
      if (!payload || typeof payload !== "object") {
        console.warn("⏸ AWB & Pickup skipped: invalid payload");
        return null;
      }
      setLoading(true);
      try {
        return await assignAwbAndPickup(payload);
      } finally {
        setLoading(false);
      }
    },

    onSuccess: (data) => {
      console.log("✅ AWB & Pickup Response:", data);

      const awbData = data?.data?.awb_assignment;
      const pickupData = data?.data?.pickup;

      if (awbData?.awb_code) {
        notifySuccess(`AWB Assigned: ${awbData.awb_code}`);
      }

      if (pickupData?.success) {
        if (pickupData?.data?.already_queued) {
          notifySuccess("Shipment already in pickup queue");
        } else {
          notifySuccess("Pickup scheduled successfully!");
        }
      } else if (pickupData?.error) {
        notifyError(pickupData?.message || "Pickup scheduling failed");
      }

      navigate("/orders?tab=manifest");
    },

    onError: (error) => {
      setLoading(false);

      const errorMsg = error.response?.data?.message || error.message;

      if (error.message === "Token not found" || error.response?.status === 401) {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError(errorMsg || "Failed to assign AWB and schedule pickup");
      }
    },

    onSettled: () => setLoading(false),
    retry: false,
  });
};

// ---------------------- Ship Order With Partner ----------------------
const shipOrderWithPartner = async (payload) => {
  const token = getTokenOrThrow();

  const res = await fetch(`${BACKEND_URL}/orders/ship-and-manifest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message);
  }

  notifySuccess("Order shipped successfully!");
  return res.json();
};

export const useAvailablePartners = (params) => {
  return useQuery({
    queryKey: ["available-partners", params],
    queryFn: async () => {
      console.log("Fetching courier partners with params:", params);
      const { data } = await axios.post(
        `${BACKEND_URL}/orders/get-available-partners`,
        params
      );
      console.log("API response:", data);
      return data?.data?.rates || [];
    },
    enabled: !!params?.fromPincode && !!params?.toPincode,
  });
};

export const useShipOrder = (status) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (payload) => {
      setLoading(true);
      try {
        return await shipOrderWithPartner(payload);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["orders", status], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          orders: oldData.orders.map((order) =>
            order._id === data.orderId
              ? { ...order, AwbNumber: data.awb }
              : order
          ),
        };
      });
      navigate("/orders?tab=pickup");
    },
    onError: (error) => {
      setLoading(false);
      console.error("❌ Failed to ship order:", error);
      notifyError(error.message || "Failed to ship order");
    },
  });
};

// export const useDownloadLabel = () => {
//   const { setLoading } = useLoader();

//   return useMutation({
//     mutationFn: async (awbNumbers) => {
//       // If an array is passed, join into comma-separated string
//       const awbParam = Array.isArray(awbNumbers)
//         ? awbNumbers.join(",")
//         : awbNumbers;

//       setLoading(true);
//       try {
//         const response = await axios.get(
//           `${BACKEND_URL}/orders/download-label?awbNumber=${awbParam}`,
//           { responseType: "blob" }
//         );

//         const url = window.URL.createObjectURL(new Blob([response.data]));
//         const link = document.createElement("a");
//         link.href = url;
//         link.setAttribute(
//           "download",
//           Array.isArray(awbNumbers)
//             ? `Labels-${awbNumbers.length}.pdf`
//             : `Label-${awbNumbers}.pdf`
//         );
//         document.body.appendChild(link);
//         link.click();
//         link.remove();

//         notifySuccess(
//           Array.isArray(awbNumbers)
//             ? "Labels downloaded successfully!"
//             : "Label downloaded successfully!"
//         );

//         return response.data;
//       } catch (error) {
//         console.error("❌ Failed to download label(s):", error);
//         notifyError(
//           error.response?.data?.message || "Failed to download label(s)"
//         );
//         throw error;
//       } finally {
//         setLoading(false);
//       }
//     },
//   });
// };

export const useDownloadLabel = () => {
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (shipmentIds) => {
      setLoading(true);

      try {
        const appToken = localStorage.getItem("token");
        if (!appToken) {
          notifyError("❌ User token missing. Please login again.");
          return;
        }

        // Convert to array
        const shipmentArray = Array.isArray(shipmentIds)
          ? shipmentIds
          : [shipmentIds];

        // Validate — shipment_id must be digits only
        const invalidIds = shipmentArray.filter((id) => !/^\d+$/.test(id));
        if (invalidIds.length > 0) {
          notifyError(`❌ Invalid shipment ID: ${invalidIds.join(", ")}`);
          return;
        }

        // Convert to number (backend expects number)
        const numericShipmentIds = shipmentArray.map((id) => Number(id));

        // API CALL to backend
        const response = await axios.post(
          `${BACKEND_URL}/orders/download-label`,
          { shipment_id: numericShipmentIds },
          {
            headers: {
              Authorization: `Bearer ${appToken}`,
              "Content-Type": "application/json",
            },
            responseType: "blob", // ⭐ IMPORTANT
          }
        );

        // Create download from blob
        const url = window.URL.createObjectURL(new Blob([response.data]));

        const link = document.createElement("a");
        link.href = url;
        link.download =
          numericShipmentIds.length === 1
            ? `Label-${numericShipmentIds[0]}.pdf`
            : "Labels.pdf";

        document.body.appendChild(link);
        link.click();
        link.remove();

        notifySuccess("Label downloaded successfully!");
      } catch (error) {
        notifyError(
          error?.response?.data?.message || "Failed to download label"
        );
      } finally {
        setLoading(false);
      }
    },
  });
};

export const useGenerateManifest = () => {
  const token = localStorage.getItem("authToken");
  const queryClient = useQueryClient();
  const { setLoading } = useLoader(); // Optional, if using a global loader

  return useMutation({
    mutationFn: async (orderId) => {
      setLoading?.(true); // ✅ Optional global loader ON
      try {
        const res = await fetch(`${BACKEND_URL}/orders/generate-manifest`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // ✅ Add token here
          },
          body: JSON.stringify({ orderId }),
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error?.message || "Failed to generate manifest");
        }

        return await res.json(); // return manifest response
      } finally {
        setLoading?.(false); // ✅ Optional global loader OFF
      }
    },

    onSuccess: (data) => {
      // console.log("✅ Manifest generated:", data);
      notifySuccess("Manifest generated successfully!");

      // Optionally refetch related queries
      queryClient.invalidateQueries(["orders"]);
    },

    onError: (error) => {
      console.error("❌ Manifest generation failed:", error);
      notifyError(error.message || "Failed to generate manifest");
    },
  });
};

export const useDownloadManifest = () => {
  return useMutation({
    mutationFn: async (shipmentIds) => {
      // === Validate ===
      if (!shipmentIds || shipmentIds.length === 0) {
        throw new Error("Shipment ID is required");
      }

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("User token missing");
      }

      console.log("Sending Manifest Request:", shipmentIds);

      // === API CALL ===
      const res = await fetch(`${BACKEND_URL}/orders/download-manifest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shipment_id: Array.isArray(shipmentIds) ? shipmentIds : [shipmentIds],
        }),
      });

      console.log("Manifest Response:", res);

      // === Handle Server Error ===
      if (!res.ok) {
        const errorMsg = await res.text();
        throw new Error(errorMsg || "Failed to download manifest");
      }

      // === Parse JSON Response ===
      const data = await res.json();

      if (!data.success || !data.manifest_url) {
        throw new Error("Manifest URL not found in response");
      }

      // === Download PDF from URL ===
      const manifestUrl = data.manifest_url;

      // Fetch the actual PDF from the manifest URL
      const pdfRes = await fetch(manifestUrl);

      if (!pdfRes.ok) {
        throw new Error("Failed to fetch manifest PDF from URL");
      }

      const blob = await pdfRes.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `manifest-${Array.isArray(shipmentIds) ? shipmentIds.join("-") : shipmentIds}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      window.URL.revokeObjectURL(url);

      return { shipmentIds, manifest_url: manifestUrl };
    },

    onSuccess: () => {
      notifySuccess("Manifest downloaded successfully!");
    },

    onError: (err) => {
      notifyError(`❌ Download failed: ${err.message}`);
    },
  });
};

export const useSearchOrders = (search) => {
  const token = localStorage.getItem("token");
  const adminId = localStorage.getItem("userId");

  return useQuery({
    queryKey: ["search-orders", search],
    enabled: !!search, // Only fetch when search exists
    queryFn: async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/orders/all-orders/${adminId}`,
          {
            params: {
              search, // search across all orders
              tab: "", // no tab filtering when searching
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        return Array.isArray(data?.orders) ? data.orders : [];
      } catch (error) {
        console.error("Search error:", error);
        throw error;
      }
    },
    staleTime: 0,
    cacheTime: 0,
    initialData: [],
    retry: 1,
  });
};

export const useExportOrders = () => {
  return useMutation({
    mutationFn: async ({ startDate, endDate, usersid }) => {
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      const userId = localStorage.getItem("userId");

      const response = await axios.get(`${BACKEND_URL}/orders/export-orders`, {
        params: {
          startDate,
          endDate,
          // Only send userId if not admin
          ...(role !== "admin" && usersid ? { userId: usersid } : {})
        },
        headers: {
          Authorization: `Bearer ${token}`,
          userid: userId,  // Backend expects this header
          role: role,      // Backend expects this header
        },
        responseType: "blob",
      });

      // Convert blob to downloadable file
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "orders-report.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url); // Clean up
    },
  });
};

const ndrReattempt = async (payload) => {
  const { data } = await axios.post(
    `${BACKEND_URL}/orders/ndr-reattempt`,
    payload
  );
  return data;
};

export const useNdrReattempt = () => {
  return useMutation({
    mutationFn: ndrReattempt,
    onSuccess: (response) => {
      notifySuccess(
        response?.message || "Reattempt request sent successfully!"
      );
    },
    onError: (error) => {
      notifyError(
        error?.response?.data?.message || "Failed to send reattempt request"
      );
    },
  });
};

const postDownloadStatus = async (body) => {
  const response = await axios.post(
    `${BACKEND_URL}/orders/download-status`,
    body
  );
  return response.data;
};

export const useDownloadStatusMutation = () => {
  return useMutation({
    mutationFn: postDownloadStatus,
    onSuccess: (data) => {
      notifySuccess("Order Downloaded");
    },
    onError: (error) => {
      notifyError("❌ Error:", error);
    },
  });
};

// ---------------------- RTO Accept ----------------------
const rtoAccept = async ({ shipment_id }) => {
  const token = getTokenOrThrow();

  const { data } = await axios.post(
    `${BACKEND_URL}/warehouse/rto-accept`,
    { shipment_id },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return data;
};

export const useRtoAccept = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rtoAccept,

    onSuccess: () => {
      notifySuccess("Order accepted successfully");
      queryClient.invalidateQueries(["allOrders"]);
    },

    onError: (err) => {
      notifyError(err?.response?.data?.message || "Failed to accept order");
    },
  });
};

const rtoReject = async ({ shipment_id, remark }) => {
  const token = getTokenOrThrow();

  const { data } = await axios.post(
    `${BACKEND_URL}/warehouse/rto-reject`,
    { shipment_id, remark },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return data;
};

export const useRtoReject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rtoReject,

    onSuccess: () => {
      notifySuccess("Order rejected successfully");
      queryClient.invalidateQueries(["allOrders"]);
    },

    onError: (err) => {
      notifyError(err?.response?.data?.message || "Failed to reject order");
    },
  });
};

const fetchDashboard = async ({ startDate, endDate }) => {
  const token = getTokenOrThrow();

  const role = localStorage.getItem("role");
  const userid = localStorage.getItem("userId");

  const { data } = await axios.get(`${BACKEND_URL}/orders/dashboard`, {
    params: { startDate, endDate },
    headers: {
      Authorization: `Bearer ${token}`,
      role: role, // ✅ Added
      userid: userid, // ✅ Added
    },
  });

  return data.data;
};

export const useDashboard = ({ startDate, endDate }) => {
  return useQuery({
    queryKey: ["dashboard", startDate, endDate],
    queryFn: () => fetchDashboard({ startDate, endDate }),
    keepPreviousData: true,
  });
};

const getUserOrders = async ({ userId, page = 1, limit = 20 }) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");

  const { data } = await axios.get(`${BACKEND_URL}/orders/all-orders`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      userId,
      page,
      limit,
    },
  });

  return data;
};

export const useUserOrders = ({ userId, page = 1, limit = 20 }) => {
  return useQuery({
    queryKey: ["userOrders", userId, page, limit],
    queryFn: () => getUserOrders({ userId, page, limit }),
    enabled: !!userId, // only run if we actually have a userId
    keepPreviousData: true,
    onError: (err) => {
      console.error("Error fetching user orders:", err);
      notifyError("Failed to fetch user orders");
    },
  });
};

const verifyOrderAPI = async (orderId) => {
  const token = getTokenOrThrow();
  const { data } = await axios.patch(
    `${BACKEND_URL}/orders/${orderId}/verify`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const useVerifyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyOrderAPI,
    onSuccess: (data) => {
      notifySuccess("Order verified successfully!");
      // Invalidate related queries to refetch data
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Verify order failed:", error);
      notifyError(error.response?.data?.message || "Failed to verify order");
    },
  });
};

const declineOrderAPI = async ({ orderId, remarks }) => {
  const token = getTokenOrThrow();
  const { data } = await axios.put(
    `${BACKEND_URL}/orders/${orderId}/decline`,
    { remarks }, // Send remarks in the request body
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const useDeclineOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: declineOrderAPI,
    onSuccess: (data) => {
      notifySuccess("Order declined successfully!");
      // Invalidate related queries to refetch data
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Decline order failed:", error);
      notifyError(error.response?.data?.message || "Failed to decline order");
    },
  });
};

const reverifyOrderAPI = async (orderId) => {
  const token = getTokenOrThrow();
  const { data } = await axios.patch(
    `${BACKEND_URL}/orders/${orderId}/reverify`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const useReverifyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reverifyOrderAPI,
    onSuccess: (data) => {
      notifySuccess("Order status reset successfully!");
      // Invalidate related queries to refetch data
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Reverify order failed:", error);
      notifyError(
        error.response?.data?.message || "Failed to reset order status"
      );
    },
  });
};

const updatePaymentMethodAPI = async ({
  orderId,
  methodIndex,
  paymentOption,
  paymentAmount,
  paymentProofImages,
}) => {
  const token = getTokenOrThrow();

  const formData = new FormData();
  formData.append("methodIndex", methodIndex);
  if (paymentOption) formData.append("paymentOption", paymentOption);
  if (paymentAmount !== undefined)
    formData.append("paymentAmount", paymentAmount);

  // Append new image files if any
  if (paymentProofImages && paymentProofImages.length > 0) {
    paymentProofImages.forEach((file) => {
      formData.append("paymentProofImages", file);
    });
  }

  const { data } = await axios.put(
    `${BACKEND_URL}/orders/${orderId}/payment-method`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return data;
};

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePaymentMethodAPI,
    onSuccess: (data) => {
      notifySuccess("Payment method updated successfully!");
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Update payment method failed:", error);
      notifyError(
        error.response?.data?.error || "Failed to update payment method"
      );
    },
  });
};

const deletePaymentImageAPI = async ({ orderId, methodIndex, imageUrl }) => {
  const token = getTokenOrThrow();

  const { data } = await axios.delete(
    `${BACKEND_URL}/orders/${orderId}/payment-method`,
    {
      headers: { Authorization: `Bearer ${token}` },
      data: { methodIndex, imageUrl },
    }
  );
  return data;
};

export const useDeletePaymentImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePaymentImageAPI,
    onSuccess: (data) => {
      notifySuccess("Image deleted successfully!");
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Delete image failed:", error);
      notifyError(error.response?.data?.error || "Failed to delete image");
    },
  });
};

export const useTrackOrder = () => {
  return useMutation({
    mutationFn: async (awbNumber) => {
      const token = localStorage.getItem("token"); // ✅ fetch token

      const res = await axios.get(
        `${BACKEND_URL}/orders/track-order/${awbNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // ✅ send token
          },
        }
      );

      return res.data;
    },

    onSuccess: (data) => {
      notifySuccess(data.message || "Tracking fetched successfully");

      const trackUrl = data?.tracking_data?.track_url;

      if (trackUrl) {
        window.open(trackUrl, "_blank");
      }
    },

    onError: (error) => {
      notifyError(
        error?.response?.data?.message || "Unable to fetch shipment tracking"
      );
    },
  });
};
